// Joining share counts to prices.
//
// The one rule that governs this file: **price and share count must be on the same
// split basis at every point in time.** Massive's `adjusted=true` prices are restated
// onto today's basis, so every filed share count is multiplied forward by the splits
// that happened after its as-of date. Market cap is invariant to splits, so once both
// sides agree the product is the real historical market cap.
//
// The join is a step function. A filing tells us the count on one specific day; it
// tells us nothing about the days in between, so the count simply holds until the next
// filing says otherwise. Smoothing it would invent daily precision we don't have.

/**
 * Cumulative share-count multiplier for splits taking effect strictly after `asOfDate`.
 * A 1-for-100 reverse split returns 0.01, so `shares * factor` is the count on today's basis.
 */
export function splitFactorAfter(splits, asOfDate) {
  return (splits ?? [])
    .filter((s) => s.execution_date > asOfDate && s.split_from > 0 && s.split_to > 0)
    .reduce((factor, s) => factor * (s.split_to / s.split_from), 1);
}

/** Splits that landed after `asOfDate`, described for the UI. */
export function splitsAfter(splits, asOfDate) {
  return (splits ?? []).filter((s) => s.execution_date > asOfDate && s.split_from > 0 && s.split_to > 0);
}

/** A daily bar's trading date. Massive stamps `t` at midnight Eastern, so the UTC date matches. */
export function barDate(bar) {
  return new Date(bar.t).toISOString().slice(0, 10);
}

/**
 * Filing rows that carry a usable number, split-adjusted and sorted oldest first.
 *
 * When two filings report the same as-of date — a 10-K and its amendment, most often —
 * the one filed later wins, since that is the corrected number.
 */
export function buildObservations(filings, splits) {
  const usable = (filings ?? []).filter((f) => Number.isFinite(f.shares) && f.shares > 0 && f.asOfDate);

  const byAsOf = new Map();
  for (const f of usable) {
    const existing = byAsOf.get(f.asOfDate);
    if (!existing || f.filingDate > existing.filingDate) byAsOf.set(f.asOfDate, f);
  }

  return [...byAsOf.values()]
    .sort((a, b) => (a.asOfDate < b.asOfDate ? -1 : a.asOfDate > b.asOfDate ? 1 : 0))
    .map((f) => {
      const splitFactor = splitFactorAfter(splits, f.asOfDate);
      const adjustedShares = f.shares * splitFactor;
      return {
        ...f,
        splitFactor,
        adjustedShares,
        // The second basis mismatch, after splits. A depositary receipt is a claim on
        // `adsRatio` ordinary shares, so the price is quoted per ADS while the filing
        // counts ordinary shares; dividing puts the count in the units the price is in.
        // Total equity value is unchanged by the ratio — which is the point: it is the
        // same company either way, and only the unit differs.
        adsRatio: f.adsRatio ?? null,
        tradableShares: adjustedShares / (f.adsRatio ?? 1),
        splitsApplied: splitsAfter(splits, f.asOfDate),
      };
    });
}

/**
 * The daily market-cap series.
 *
 * Days before the first observation's as-of date are dropped rather than back-filled:
 * we genuinely do not know the share count then, and drawing a flat line backwards
 * would present a guess as data.
 */
export function buildSeries(bars, observations, windowStart) {
  if (!bars?.length || !observations?.length) return [];

  const series = [];
  let i = -1;

  for (const bar of bars) {
    const date = barDate(bar);
    if (windowStart && date < windowStart) continue;

    while (i + 1 < observations.length && observations[i + 1].asOfDate <= date) i++;
    if (i < 0) continue;

    const obs = observations[i];
    series.push({
      date,
      close: bar.c,
      // `tradableShares`, not `adjustedShares`: for an ADR the two differ by the ADS
      // ratio, and only the former is on the same basis as `bar.c`.
      shares: obs.tradableShares,
      marketCap: bar.c * obs.tradableShares,
      ordinaryShares: obs.adjustedShares,
      adsRatio: obs.adsRatio,
      accessionNumber: obs.accessionNumber,
      form: obs.form,
      asOfDate: obs.asOfDate,
    });
  }

  return series;
}

/**
 * Ratios a share count jumps by when a split has NOT been accounted for. Real
 * quarter-to-quarter changes come from issuance and buybacks and land on arbitrary
 * numbers; a split lands on a clean whole ratio, which is what makes this separable
 * from a company that genuinely issued a lot of stock.
 */
const SPLIT_RATIOS = [2, 3, 4, 5, 6, 7, 8, 10, 12, 15, 20, 25, 30, 50, 100];
/** How close to a whole ratio a jump must be. A dilutive raise essentially never
 *  lands within 2% of exactly 10.000×; a missed 10-for-1 split lands exactly on it. */
const SPLIT_RATIO_TOLERANCE = 0.02;

function matchSplitRatio(ratio) {
  for (const n of SPLIT_RATIOS) {
    if (Math.abs(ratio / n - 1) <= SPLIT_RATIO_TOLERANCE) return `${n}-for-1`;
    if (Math.abs(ratio * n - 1) <= SPLIT_RATIO_TOLERANCE) return `1-for-${n}`;
  }
  return null;
}

/**
 * Split-shaped jumps left in the **split-adjusted** series.
 *
 * If the split data is right, adjusting each filed count forward makes the series
 * continuous — a 10-for-1 split shows up as no change at all, because both sides are
 * already on today's basis. So a clean whole-ratio jump that survives adjustment means
 * a split is missing or wrong, and every market cap before that date is off by exactly
 * that factor. This is the check that catches a splits outage even when nothing
 * errored, and it also catches a split the vendor simply doesn't have.
 */
export function unaccountedSplits(observations) {
  const found = [];
  for (let i = 1; i < observations.length; i++) {
    const prev = observations[i - 1];
    const curr = observations[i];
    // The price basis, not the ordinary one. When a depositary bank changes the ADS
    // ratio the vendor records it as a split, so the split-adjusted ordinary count steps
    // by that factor while no ordinary share was ever split — `tradableShares` divides it
    // back out and stays flat, which is the continuity this check is actually asserting.
    // For everything else the two are identical.
    if (!prev.tradableShares || !curr.tradableShares) continue;

    const ratio = curr.tradableShares / prev.tradableShares;
    const label = matchSplitRatio(ratio);
    if (label) found.push({ from: prev, to: curr, ratio, label });
  }
  return found;
}

/** Headline stats for the series — first/last/high/low and the change between the ends. */
export function summarize(series) {
  if (!series.length) return null;
  const first = series[0];
  const last = series[series.length - 1];
  let high = first;
  let low = first;
  for (const p of series) {
    if (p.marketCap > high.marketCap) high = p;
    if (p.marketCap < low.marketCap) low = p;
  }
  return {
    first,
    last,
    high,
    low,
    changePct: first.marketCap ? ((last.marketCap - first.marketCap) / first.marketCap) * 100 : null,
  };
}

/**
 * The market-cap floor worth watching on this desk. A company below it is in a
 * different conversation than one above it, so the chart draws it and the series is
 * measured against it.
 */
export const MICRO_CAP_THRESHOLD = 5e6;

/**
 * Further round levels for the chart, drawn only where the scale already reaches them.
 *
 * Unlike the threshold above, these never pull the axis: a company topping out at $7M
 * would have two thirds of its plot given over to empty space above the data to show a
 * $20M rule it never approaches. They are orientation marks, not a subject of the page,
 * so they appear when they happen to be in view and are silent otherwise.
 */
export const REFERENCE_LEVELS = [10e6, 20e6];

/** Inclusive calendar days from `a` to `b`, both `YYYY-MM-DD`. Parsed by hand — see format.js. */
function calendarSpan(a, b) {
  const [ay, am, ad] = a.split('-').map(Number);
  const [by, bm, bd] = b.split('-').map(Number);
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86400000) + 1;
}

/**
 * Runs of consecutive days where market cap closed below `threshold`.
 *
 * Two counts are reported per stretch and they are not interchangeable. `tradingDays`
 * is what we actually observed — one per daily bar — and is the honest measure, since
 * the series only contains days the market was open. `calendarDays` is the wall-clock
 * span from the first such close to the last, which is what "they were under for six
 * months" usually means. A stretch breaks the moment one close comes back above the
 * line, so a single day above splits one long period into two: that is deliberate, a
 * brief recovery is a real event and hiding it would overstate continuity.
 *
 * Days before the first filed share count carry no market cap at all and are absent
 * from the series, so they are counted in neither direction — `observedDays` says how
 * much of the window was actually measurable.
 *
 * `runsToLastClose` says the final stretch reaches the end of the data — which is not
 * the same as "still true today". A delisted or suspended ticker returns bars ending
 * on its last trading day, years ago, and nothing downstream checks that the series
 * reaches the present. So `lastDate` comes back with it, and every claim built on this
 * has to be dated rather than stated in the present tense.
 */
export function belowThreshold(series, threshold = MICRO_CAP_THRESHOLD) {
  const stretches = [];
  let current = null;

  for (const point of series) {
    if (Number.isFinite(point.marketCap) && point.marketCap < threshold) {
      if (!current) current = { start: point.date, end: point.date, tradingDays: 0, low: point };
      current.end = point.date;
      current.tradingDays += 1;
      if (point.marketCap < current.low.marketCap) current.low = point;
    } else if (current) {
      stretches.push(current);
      current = null;
    }
  }
  if (current) stretches.push(current);

  const withSpans = stretches.map((s) => ({ ...s, calendarDays: calendarSpan(s.start, s.end) }));
  const lastDate = series.length ? series[series.length - 1].date : null;

  return {
    threshold,
    stretches: withSpans,
    tradingDays: withSpans.reduce((sum, s) => sum + s.tradingDays, 0),
    calendarDays: withSpans.reduce((sum, s) => sum + s.calendarDays, 0),
    longest: withSpans.reduce((best, s) => (!best || s.tradingDays > best.tradingDays ? s : best), null),
    lastDate,
    runsToLastClose: Boolean(lastDate && withSpans.length && withSpans[withSpans.length - 1].end === lastDate),
    observedDays: series.length,
  };
}

/** Thin a daily series for the table. "daily" returns it untouched. */
export function resample(series, frequency) {
  if (frequency === 'daily' || series.length === 0) return series;

  const keyOf = (date) => (frequency === 'monthly' ? date.slice(0, 7) : isoWeek(date));
  const out = [];
  // Keep the LAST bar in each bucket — the period's closing value, which is what a
  // weekly or monthly market-cap reading conventionally means.
  for (let i = 0; i < series.length; i++) {
    const isLastOfBucket = i === series.length - 1 || keyOf(series[i + 1].date) !== keyOf(series[i].date);
    if (isLastOfBucket) out.push(series[i]);
  }
  return out;
}

/** ISO-ish week key, "2026-W31". Only ever used to group adjacent days, so exactness isn't critical. */
function isoWeek(date) {
  const [y, m, d] = date.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const day = dt.getUTCDay() || 7;
  dt.setUTCDate(dt.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(dt.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((dt - yearStart) / 86400000 + 1) / 7);
  return `${dt.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
}
